import { useEffect, useState, useMemo } from "react";

/**
 * Small typewriter that cycles through `phrases`, typing each character
 * with a short jitter, holds it for `holdMs`, erases, and moves on.
 * Respects `prefers-reduced-motion` — falls back to just showing the
 * first phrase steady.
 *
 * Props:
 *   phrases  — string[] to cycle through
 *   typeMs   — ms per typed character (default 55)
 *   eraseMs  — ms per erased character (default 30)
 *   holdMs   — ms to hold the full phrase before erasing (default 1700)
 *   className — passed through to the wrapper span
 *   reserveSpace — if true, reserves the layout height of the tallest
 *     possible phrase wrap-state by rendering all phrases stacked
 *     (CSS-grid) with only the live one visible. Prevents the body
 *     below the H1 from "pumping" on narrow viewports where each
 *     phrase wraps to a different number of lines. Default true.
 */
export default function TypewriterCycle({
  phrases,
  typeMs = 55,
  eraseMs = 30,
  holdMs = 1700,
  className = "",
  caretClassName = "",
  testid,
  reserveSpace = true,
}) {
  const safePhrases = useMemo(() => phrases.filter(Boolean), [phrases]);
  const reduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  const [index, setIndex] = useState(0);
  const [text, setText] = useState(reduced ? safePhrases[0] || "" : "");
  const [phase, setPhase] = useState("typing"); // "typing" | "holding" | "erasing"

  useEffect(() => {
    if (reduced || safePhrases.length === 0) return;
    const current = safePhrases[index];
    let timer;
    if (phase === "typing") {
      if (text.length < current.length) {
        timer = setTimeout(() => setText(current.slice(0, text.length + 1)), typeMs);
      } else {
        timer = setTimeout(() => setPhase("erasing"), holdMs);
      }
    } else if (phase === "erasing") {
      if (text.length > 0) {
        timer = setTimeout(() => setText(current.slice(0, text.length - 1)), eraseMs);
      } else {
        timer = setTimeout(() => {
          setIndex((i) => (i + 1) % safePhrases.length);
          setPhase("typing");
        }, 150);
      }
    }
    return () => clearTimeout(timer);
  }, [text, phase, index, safePhrases, typeMs, eraseMs, holdMs, reduced]);

  // Live caret (only shown when motion is allowed)
  const caret = !reduced && (
    <span
      aria-hidden="true"
      className={`typewriter-caret inline-block w-[0.08em] ml-1 align-baseline ${caretClassName}`}
    >|</span>
  );

  if (!reserveSpace) {
    return (
      <span className={className} data-testid={testid} aria-live="polite">
        <span>{text}</span>
        {caret}
      </span>
    );
  }

  // ── Layout-stable mode ──
  // Render every phrase stacked in the same grid cell so the parent's height
  // = the tallest possible wrap-state of any phrase. Only one cell is visible
  // at a time (the live one); the rest are visibility:hidden so they still
  // occupy space but aren't read by screen-readers.
  return (
    <span
      className={`typewriter-stack ${className}`}
      data-testid={testid}
      aria-live="polite"
      style={{ display: "inline-grid", gridTemplateAreas: '"stack"' }}
    >
      {safePhrases.map((p, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{ gridArea: "stack", visibility: "hidden" }}
        >
          {p}
        </span>
      ))}
      <span style={{ gridArea: "stack" }}>
        <span>{text}</span>
        {caret}
      </span>
    </span>
  );
}
